import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { ChangeSet, EditorState, Transaction } from "@codemirror/state";
import {
  EditorView,
  keymap,
  placeholder as cmPlaceholder,
  ViewPlugin,
  ViewUpdate,
} from "@codemirror/view";
import { action, IComputedValue, reaction, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import { ActionType, Pane, PaneType } from "../Types";
import { assert, getParentByFilter, throttle } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import { activePaneStates } from "./CanvasState";
import {
  presenceDispatchPlugin,
  subscribeCodemirrorPaneId,
} from "./CodeMirrorPresence";
import {
  cmBaseTheme,
  PaneEditorRemoteUpdateEmitter,
  useCodemirrorVim,
  vimCompartment,
} from "./ExpressionEditor";
import { PaneBody } from "./PaneBody";
import { PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";
import { applicationState } from "./Sync";

export const cmExtensions = [
  cmBaseTheme,
  EditorView.theme({
    ".cm-scroller": { fontFamily: "Inter" },
  }),
  history(),
  keymap.of([
    ...defaultKeymap,
    ...historyKeymap,
    {
      key: "Escape",
      run: (editorView) => {
        const focusableParent = getParentByFilter(
          editorView.dom,
          (element) => element.getAttribute("tabIndex") !== null
        );
        focusableParent!.focus();
        return true;
      },
    },
  ]),
  EditorView.lineWrapping,
];

function TextEditor({
  paneId,
  initialValue,
  focusOnMount,
}: {
  paneId: string;
  initialValue: string;
  focusOnMount: boolean;
}) {
  const cmViewRef = useRef<EditorView | undefined>();
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const rootElement = rootRef.current!;
    const cmView = new EditorView({
      state: EditorState.create({
        doc: initialValue,
        extensions: [
          vimCompartment.of([]),
          ...cmExtensions,
          cmPlaceholder("type something"),
          ViewPlugin.fromClass(
            class {
              update(update: ViewUpdate) {
                const isRemote = update.transactions.some((transaction) =>
                  transaction.annotation(Transaction.remote)
                );
                if (!isRemote && !update.changes.empty) {
                  canvasDispatch([
                    ActionType.UpdatePaneCodemirror,
                    {
                      paneId,
                      v: applicationState.server?.[1] ?? 0,
                      f: "text",
                      changes: update.changes.toJSON(),
                    },
                  ]);
                }
              }
            }
          ),
          presenceDispatchPlugin(paneId),
        ],
      }),
      parent: rootElement,
    });
    cmViewRef.current = cmView;
    if (focusOnMount) {
      cmView.focus();
    }

    function onRemoteUpdate({
      paneField,
      changes,
    }: {
      paneField: "expression" | "text";
      changes: ["changes", ChangeSet] | ["value", string];
    }) {
      if (paneField === "text") {
        const newState = cmView.state.update({
          changes:
            changes[0] === "changes"
              ? changes[1]
              : {
                  from: 0,
                  to: cmView.state.doc.length,
                  insert: changes[1],
                },
          annotations: [Transaction.remote.of(true)],
        });
        cmView.dispatch(newState);
      }
    }
    PaneEditorRemoteUpdateEmitter.addListener(paneId, onRemoteUpdate);
    const unsub = subscribeCodemirrorPaneId(paneId, cmView);
    return () => {
      PaneEditorRemoteUpdateEmitter.removeListener(paneId, onRemoteUpdate);
      cmView.destroy();
      unsub();
    };
  }, []);
  useCodemirrorVim(cmViewRef);

  return <div className="h-full select-text" ref={rootRef}></div>;
}

export const PaneText = observer(
  ({
    paneId,
    paneComputed,
    isMaximized,
    focusOnMount,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.InputText }> | undefined
    >;
    isMaximized: boolean;
    focusOnMount: boolean;
  }) => {
    const activePaneState = activePaneStates.get(paneId)!;
    assert(activePaneState.type === PaneType.InputText);
    const outputBox = activePaneState.outputBoxes[0];
    const [updatePaneThrottled] = useState(() =>
      throttle(
        action((text: string) => {
          outputBox.set(["value", text]);
        }),
        (text) => (text.length > 1000 ? 500 : 150)
      )
    );
    useEffect(
      () =>
        reaction(
          () => paneComputed.get()?.text,
          (paneText) => {
            if (paneText !== undefined) {
              updatePaneThrottled(paneText);
            }
          }
        ),
      []
    );
    useEffect(() => () => updatePaneThrottled.cancel(), []);

    const viewMode = activePaneState.viewMode.get();

    return (
      <>
        <PaneHeaderBar
          paneId={paneId}
          paneComputed={paneComputed}
          isMaximized={isMaximized}
        >
          <div className="grow" />
          {!isMaximized ? <PaneTransformButton paneId={paneId} /> : null}
        </PaneHeaderBar>
        <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
          <TextEditor
            paneId={paneId}
            initialValue={untracked(() => paneComputed.get()?.text ?? "")}
            focusOnMount={focusOnMount}
          />
        </PaneBody>
      </>
    );
  }
);
